import React, { useState } from 'react';
import { PlusIcon, PencilIcon, Trash2Icon, ArrowRightIcon } from 'lucide-react';
import { Business } from '../../data';
import { useContent } from '../../ContentContext';
import { Field, TextArea, Modal, PrimaryButton, GhostButton } from './ui';
const EMPTY: Business = {
  slug: '',
  name: '',
  place: '',
  services: '',
  description: [''],
  cover: '',
  products: [],
  perfectFor: [],
  status: 'active',
  contact: {
    address: '',
    email: '',
    instagram: ''
  }
};
function slugify(value: string) {
  return value.toLowerCase().replace(/[åä]/g, 'a').replace(/ö/g, 'o').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
export function PartnerManager() {
  const {
    businesses,
    addBusiness,
    updateBusiness,
    deleteBusiness
  } = useContent();
  const [editing, setEditing] = useState<Business | null>(null);
  const [isNew, setIsNew] = useState(false);
  const openNew = () => {
    setEditing({
      ...EMPTY,
      description: ['']
    });
    setIsNew(true);
  };
  const openEdit = (business: Business) => {
    setEditing({
      ...business
    });
    setIsNew(false);
  };
  const save = () => {
    if (!editing) return;
    const slug = editing.slug || slugify(editing.name);
    const payload: Business = {
      ...editing,
      slug
    };
    if (isNew) {
      addBusiness(payload);
    } else {
      updateBusiness(editing.slug, payload);
    }
    setEditing(null);
  };
  const set = <K extends keyof Business,>(key: K, value: Business[K]) => setEditing(prev => prev ? {
    ...prev,
    [key]: value
  } : prev);
  return <div data-id="element-75" data-mp="3k9kf6qxlnxg7">
      <div className="flex items-center justify-between" data-id="element-76" data-mp="3k9tvtnwr8s98">
        <div data-id="element-77" data-mp="3ffg6a24rxfch">
          <h1 className="font-display text-3xl font-medium text-gray-900" data-id="element-78" data-mp="3fey2mkfs7yuj">Partners</h1>
          <p className="mt-2 text-sm text-gray-500" data-id="element-79" data-mp="3bee2d7zwwf46">
            Skapa och redigera verksamheterna som visas på startsidan och i utbudet.
          </p>
        </div>
        <PrimaryButton onClick={openNew} data-id="element-80" data-mp="70fnjyrfbj1f">
          <span className="flex items-center gap-2" data-id="element-81" data-mp="6zea5nmxqkgj">
            <PlusIcon className="h-4 w-4" data-id="element-82" data-mp="2189sb4s0nh1o" /> Ny partner
          </span>
        </PrimaryButton>
      </div>

      {/* Live preview – exakt samma kort som på startsidan */}
      <div className="mt-10 grid gap-8 sm:grid-cols-2 xl:grid-cols-3" data-id="element-83" data-mp="269qpap7dqjcz">
        {businesses.map(business => <div key={business.slug} className="flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-premium" data-id="element-84" data-mp="1whtdphcglpdq">
            <div className="relative h-44 overflow-hidden bg-gray-100" data-id="element-85" data-mp="1ii23lrp2ct50">
              {business.cover && <img src={business.cover} alt={business.name} className="h-full w-full object-cover" data-id="element-86" data-mp="1nhxzf3nlxe9h" />}
              {business.status === 'coming-soon' && <div className="absolute right-4 top-4 rounded-full bg-black/70 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-md" data-id="element-87" data-mp="18m8l7rony5fb">
                  Kommer snart
                </div>}
            </div>
            <div className="flex flex-1 flex-col p-6" data-id="element-88" data-mp="37xhxy0ybwaau">
              <div className="flex items-center justify-between" data-id="element-89" data-mp="37uvt492to8wb">
                <h3 className="font-display text-xl font-medium text-gray-900" data-id="element-90" data-mp="wafv4sm6avh8">{business.name}</h3>
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-50 text-gray-400" data-id="element-91" data-mp="rcedmc4eb47v">
                  <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-92" data-mp="1610uwjdl261q" />
                </div>
              </div>
              <p className="mt-1 text-xs font-medium text-gray-500 line-clamp-1" data-id="element-93" data-mp="117ocws3esxhy">{business.services}</p>
              <p className="mt-3 text-xs text-gray-500" data-id="element-94" data-mp="1f2h1sh56vq17">{business.products.length} produkter</p>

              <div className="mt-auto flex gap-2 pt-5" data-id="element-95" data-mp="1b0vjgg9vyjjm">
                <button onClick={() => openEdit(business)} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-gray-100 py-2.5 text-xs font-medium text-gray-900 transition-colors hover:bg-gray-200" data-id="element-96" data-mp="1oxis3gb7ay5d">
                  <PencilIcon className="h-3.5 w-3.5" data-id="element-97" data-mp="3lr3tee35vvhe" /> Redigera
                </button>
                <button onClick={() => {
              if (window.confirm(`Ta bort ${business.name} och alla dess produkter?`)) {
                deleteBusiness(business.slug);
              }
            }} aria-label={`Ta bort ${business.name}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-98" data-mp="3n813cnd1bqzf">
                  <Trash2Icon className="h-3.5 w-3.5" data-id="element-99" data-mp="34fostdc89je7" />
                </button>
              </div>
            </div>
          </div>)}
      </div>

      {editing && <Modal title={isNew ? 'Ny partner' : `Redigera ${editing.name}`} onClose={() => setEditing(null)} footer={<>
              <GhostButton onClick={() => setEditing(null)} data-id="element-101" data-mp="2lrti62bwwlpb">Avbryt</GhostButton>
              <PrimaryButton onClick={save} data-id="element-102" data-mp="2qpv0um5fej09">Spara</PrimaryButton>
            </>} data-id="element-100" data-mp="3eeeb3781d5uz">
          <div className="grid gap-5 sm:grid-cols-2" data-id="element-103" data-mp="17uqol7tfpdfr">
            <Field label="Namn" value={editing.name} onChange={v => set('name', v)} placeholder="Lilla Verket" data-id="element-104" data-mp="17u7da9yahnap" />
            <Field label="URL-slug" value={editing.slug} onChange={v => set('slug', slugify(v))} placeholder="lilla-verket" hint="Lämna tomt så genereras den från namnet." data-id="element-105" data-mp="13rkhjxyhzi88" />
            <Field label="Ort" value={editing.place ?? ''} onChange={v => set('place', v)} placeholder="Växjö" data-id="element-106" data-mp="owxtussys9tg" />
            <Field label="Tjänster" value={editing.services} onChange={v => set('services', v)} placeholder="Mackverk · Deli · Hantverksbak" data-id="element-107" data-mp="k3uzwjk9yycq" />
            <div className="sm:col-span-2" data-id="element-108" data-mp="b2mznvy77ng0">
              <Field label="Omslagsbild (URL)" value={editing.cover} onChange={v => set('cover', v)} placeholder="https://..." data-id="element-109" data-mp="666bj1r0192x" />
            </div>
            <div className="sm:col-span-2" data-id="element-110" data-mp="3nnkcsc8n8n2m">
              <TextArea label="Beskrivning" value={editing.description.join('\n\n')} onChange={v => set('description', v.split('\n\n'))} hint="Separera stycken med en tom rad." data-id="element-111" data-mp="3nqokvkp8c2yk" />
            </div>
            <div className="sm:col-span-2" data-id="element-112" data-mp="3ipitbq7b2lqm">
              <Field label="Perfekt för" value={editing.perfectFor.join(', ')} onChange={v => set('perfectFor', v.split(',').map(s => s.trim()).filter(Boolean))} placeholder="Bröllop, Konferens, Kontorsfika" hint="Kommaseparerad lista." data-id="element-113" data-mp="a8xh80jdncja" />
            </div>
            <Field label="Adress" value={editing.contact.address} onChange={v => set('contact', {
          ...editing.contact,
          address: v
        })} data-id="element-114" data-mp="5gwb6wcsyi1p" />
            <Field label="E-post" value={editing.contact.email} onChange={v => set('contact', {
          ...editing.contact,
          email: v
        })} data-id="element-115" data-mp="y0l3g55p1zf8" />
            <Field label="Instagram" value={editing.contact.instagram} onChange={v => set('contact', {
          ...editing.contact,
          instagram: v
        })} data-id="element-116" data-mp="2dpkm0ecz04xg" />
            <label className="block" data-id="element-117" data-mp="2nhgjpob59t15">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-118" data-mp="2nihyo3va6zo5">Status</span>
              <select value={editing.status ?? 'active'} onChange={e => set('status', e.target.value as Business['status'])} className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-900 outline-none focus:border-black" data-id="element-119" data-mp="1urq41atv6j2x">
                <option value="active" data-id="element-120" data-mp="3l3e822c7gsl0">Aktiv</option>
                <option value="coming-soon" data-id="element-121" data-mp="3l22w3f8lweit">Kommer snart</option>
              </select>
            </label>
          </div>
        </Modal>}
    </div>;
}